import { useState, useRef, useEffect } from 'react';

export const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function toMinutes(time) {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function toTimeString(minutes) {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
}

export function generateSlots(openTime, closeTime, durationMinutes, capacity) {
  const start = toMinutes(openTime);
  const end = toMinutes(closeTime);
  const duration = Number(durationMinutes) || 0;
  if (!(duration > 0) || end <= start) return [];

  const slots = [];
  for (let t = start; t + duration <= end; t += duration) {
    slots.push({
      _key: `gen-${t}-${Math.random().toString(36).slice(2, 8)}`,
      startTime: toTimeString(t),
      endTime: toTimeString(t + duration),
      capacity: Number(capacity) || 1,
      isActive: true,
    });
  }
  return slots;
}

export const inputClass = 'google-input-box w-full px-3.5 py-2 text-sm font-medium text-[#202124] dark:text-[#e8eaed] outline-none';

export const inp = 'google-input-box w-full px-4 py-2.5 text-sm font-medium text-[#202124] dark:text-[#e8eaed] outline-none';

export function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

export function SectionCard({ title, icon, children }) {
  return (
    <div className="google-card bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[#e8eaed] dark:border-[#3c4043]">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center text-sm shrink-0 bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8]">
          {icon}
        </div>
        <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed]">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export function MultiSelectDropdown({ label, options, selected, onChange, placeholder = 'Select...' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Keep any already-selected values that aren't in the predefined list (e.g. legacy free-text entries).
  const allOptions = [...options, ...selected.filter(s => !options.includes(s))];

  function toggle(opt) {
    onChange(selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt]);
  }

  return (
    <div className="flex flex-col gap-1.5 relative" ref={ref}>
      <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] uppercase tracking-wide">{label}</label>
      <button type="button" onClick={() => setOpen(o => !o)} className={inp + ' text-left flex items-center justify-between cursor-pointer'}>
        <span className={selected.length ? 'text-[#202124] dark:text-[#e8eaed]' : 'text-[#9aa0a6]'}>
          {selected.length ? `${selected.length} selected` : placeholder}
        </span>
        <span className="text-[#9aa0a6] text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(s => (
            <span key={s} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-[#e8f0fe] dark:bg-[#1a2e4c] text-[#1a73e8] dark:text-[#8ab4f8]">
              {s}
              <button type="button" onClick={() => toggle(s)} className="hover:text-[#EA4335] cursor-pointer">✕</button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="absolute z-20 top-full mt-1 w-full max-h-56 overflow-y-auto rounded-2xl bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] shadow-lg p-1.5 space-y-0.5">
          {allOptions.map(opt => (
            <label key={opt} className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl hover:bg-[#f1f3f4] dark:hover:bg-[#202124] cursor-pointer text-sm text-[#202124] dark:text-[#e8eaed]">
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)}
                className="w-4 h-4 accent-[#1a73e8] rounded" />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function sanitizeSlotList(slots) {
  return (slots || []).map(s => ({
    ...(s._id ? { _id: s._id } : {}),
    startTime: s.startTime,
    endTime: s.endTime,
    capacity: Number(s.capacity) || 1,
    isActive: !!s.isActive,
  }));
}

export function sanitizeWeeklySlots(weeklySlots, weekdays) {
  const out = {};
  for (const day of weekdays) {
    out[day] = sanitizeSlotList(weeklySlots[day]);
  }
  return out;
}
